import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import axios from '../../axios';
import config from '../../config';
import './BrokerSearch.css';
import { useLanguage } from '../../i18n/LanguageContext';

const BrokerSearch = () => {
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchName, setSearchName] = useState('');
  const [searchCompany, setSearchCompany] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchPerformed, setSearchPerformed] = useState(false);
  
  const history = useHistory();
  const backend_url = config.backend_url;
  const { t } = useLanguage();

  // We no longer fetch all brokers on initial load
  // useEffect(() => {
  //   fetchBrokers();
  // }, []);

  const fetchBrokers = async (page = 1, name = '', company = '') => {
    try {
      setLoading(true);
      let url = `${backend_url}/api/brokers`;
      
      // If search parameters are provided, use the search endpoint
      if (name || company) {
        url = `${backend_url}/api/brokers/search?page=${page}&per_page=12`;
        if (name) url += `&name=${encodeURIComponent(name)}`;
        if (company) url += `&company=${encodeURIComponent(company)}`;
        setSearchPerformed(true);
      } else {
        setSearchPerformed(false);
      }
      
      const response = await axios.get(url, {
        withCredentials: true
      });
      
      // Handle different response formats between search and regular endpoints
      if (name || company) {
        setBrokers(response.data.brokers);
        setTotalPages(response.data.pages);
        setCurrentPage(response.data.current_page);
      } else {
        setBrokers(response.data);
        setTotalPages(1);
        setCurrentPage(1);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching brokers:', error);
      setError(t('brokerSearch.loadError'));
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchBrokers(1, searchName, searchCompany);
  };

  const handleReset = () => {
    setSearchName('');
    setSearchCompany('');
    setSearchPerformed(false);
    setBrokers([]);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    fetchBrokers(newPage, searchName, searchCompany);
  };

  const handleBrokerClick = (brokerId) => {
    history.push(`/broker/${brokerId}`);
  };

  if (loading) return <div className="broker-search"><div className="loading">{t('brokerSearch.loading')}</div></div>;
  if (error) return <div className="broker-search"><div className="error">{error}</div></div>;

  return (
    <div className="broker-search">
      <div className="broker-search-section">
      <h2><FontAwesomeIcon icon={faSearch} className="broker-icon" /> {t('brokerSearch.title')}</h2>
        <form onSubmit={handleSearch} className="broker-search-form">
          <div className="search-inputs">
            <div className="form-group">
              <label htmlFor="name-search">{t('brokerSearch.name')}</label>
              <input
                id="name-search"
                type="text"
                placeholder={t('brokerSearch.namePlaceholder')}
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                className="search-input"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="company-search">{t('brokerProfile.company')}</label>
              <input
                id="company-search"
                type="text"
                placeholder={t('brokerSearch.companyPlaceholder')}
                value={searchCompany}
                onChange={(e) => setSearchCompany(e.target.value)}
                className="search-input"
              />
            </div>
          </div>
          
          <div className="search-buttons">
            <button type="submit" className="search-button">{t('objectDashboard.search')}</button>
            <button type="button" onClick={handleReset} className="reset-button">{t('objectDashboard.reset')}</button>
          </div>
        </form>
      </div>

      <div className="brokers-list-section">
        <h3>
          {searchPerformed 
            ? t('brokerSearch.results', { count: brokers.length }) 
            : t('brokerSearch.prompt')}
        </h3>
        
        {loading && <div className="loading-results">{t('brokerSearch.searching')}</div>}
        
        {!loading && searchPerformed ? (
          brokers.length > 0 ? (
            <>
              <div className="brokers-list">
                {brokers.map(broker => (
                  <div 
                    key={broker.id} 
                    className="broker-card" 
                    onClick={() => handleBrokerClick(broker.id)}
                  >
                    <h4 className="broker-name">
                      {(broker.firstname || broker.lastname) 
                        ? `${broker.firstname || ''} ${broker.lastname || ''}`.trim() 
                        : broker.username || t('brokerSearch.brokerFallback')}
                    </h4>
                    
                    {broker.company && broker.company !== "NULL" && (
                      <p className="broker-company">
                        <span className="company-label">{t('brokerSearch.companyLabel')}</span> {broker.company}
                        {broker.company_chain && broker.company_chain !== "NULL" && ` (${broker.company_chain})`}
                      </p>
                    )}
                    
                    {broker.description && (
                      <p className="broker-description">
                        {broker.description.length > 100 
                          ? `${broker.description.substring(0, 100)}...` 
                          : broker.description}
                      </p>
                    )}
                    <div className="broker-view-profile">{t('brokerSearch.viewProfile')}</div>
                  </div>
                ))}
              </div>
              
              {totalPages > 1 && (
                <div className="pagination">
                  <button 
                    onClick={() => handlePageChange(currentPage - 1)} 
                    disabled={currentPage === 1}
                    className="pagination-button"
                  >
                    {t('common.previous')}
                  </button>
                  <span className="pagination-info">{t('common.pageOf', { current: currentPage, total: totalPages })}</span>
                  <button 
                    onClick={() => handlePageChange(currentPage + 1)} 
                    disabled={currentPage === totalPages}
                    className="pagination-button"
                  >
                    {t('common.next')}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="no-brokers">{t('brokerSearch.noResults')}</div>
          )
        ) : (
          !loading && !searchPerformed && (
            <div className="empty-search-state">
              <div className="empty-icon">🔍</div>
              <p>{t('brokerSearch.emptyTitle')}</p>
              <p className="empty-subtext">{t('brokerSearch.emptySubtext')}</p>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default BrokerSearch; 
